import React, { useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import ModalDialog from 'components/ModalDialog';
import Mixpanel from 'utils/mixpanelService';
import { getLocalData } from 'utils/localDataStore';
import CustomAutocomplete from 'components/CustomAutocomplete';
import useSiteCopySelector from 'components/useSiteCopySelector';
import useIsCustomOnboardingEnabled from '../hooks/useIsCustomOnboardingEnabled';
import { getCustomOnboardingLocalUtils } from './utils';

const pages = {
  county: 'county',
  topic: 'topic',
};

const styles = makeStyles(() => ({
  title: {
    textAlign: 'center',
    fontWeight: 'bold',
  },
}));

const CustomOnboardingDialog = () => {
  const classes = styles();

  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [selectedCounty, setSelectedCounty] = useState();
  const [selectedTopic, setSelectedTopic] = useState();
  const [isOpen, setIsOpen] = useState(true);
  const [customOnboardingSiteCopy] = useSiteCopySelector([
    'custom-onboarding-mapping',
  ]);

  const isCustomOnboardingEnabled = useIsCustomOnboardingEnabled();

  useEffect(() => {
    if (!isCustomOnboardingEnabled) return;

    Mixpanel.track('Custom Onboarding - Viewed');
  }, [isCustomOnboardingEnabled]);

  if (!isCustomOnboardingEnabled) return null;

  const brand = getLocalData('brand');
  const customOnboardingConfig = _get(customOnboardingSiteCopy, 'pageCopy');
  const customOnBoardingClientsConfig = _get(customOnboardingConfig, [
    'clientsConfig',
    brand,
  ]);

  const clientCounties = _get(customOnBoardingClientsConfig, 'counties', []);
  const clientTopics = _get(customOnBoardingClientsConfig, 'topics', {});

  const clientCountiesOptions = clientCounties.map(county => ({
    label: county,
    value: county,
  }));

  const clientTopicsOptions = Object.keys(clientTopics).map(topic => ({
    label: topic,
    value: clientTopics[topic],
  }));

  const isFirstPage = currentPageNumber === 1;
  const isLastPage = currentPageNumber === 2;

  const getCurrentPage = () => {
    if (isFirstPage) return pages.county;
    if (isLastPage) return pages.topic;
    return null;
  };

  const currentPage = getCurrentPage();
  const { set } = getCustomOnboardingLocalUtils();

  const selectedTopicName = _get(selectedTopic, 'label', '');
  const selectedCountyName = _get(selectedCounty, 'label', '');

  const handleCustomBoardingViewed = () => {
    set(selectedCountyName);
    setIsOpen(false);

    Mixpanel.track('Custom Onboarding - Completed', {
      county: selectedCountyName,
      topic: selectedTopicName,
    });
  };

  const handleNavigateToNextPage = () => {
    if (isLastPage) {
      window.location.href = _get(selectedTopic, 'value', '');
      handleCustomBoardingViewed();
      return;
    }

    setCurrentPageNumber(currentPageNumber + 1);
  };

  const buttons = _get(customOnboardingConfig, 'buttons', {});

  const currentPageConfig = _get(customOnboardingConfig, currentPage, {});

  const title = _get(currentPageConfig, 'title', '');
  const subTitle = _get(currentPageConfig, 'subTitle', '');
  const placeholder = _get(currentPageConfig, 'placeholder', '');
  const noOptionsText = _get(currentPageConfig, 'noOptionsText', '');

  const currentPageSelectedValue =
    currentPage === pages.county ? selectedCounty : selectedTopic;

  const modalButtons = [
    {
      label: _get(buttons, 'next'),
      color: 'primary',
      variant: 'contained',
      onClick: handleNavigateToNextPage,
      disabled: _isEmpty(_get(currentPageSelectedValue, 'value')),
    },
  ];

  return (
    <ModalDialog
      open={isOpen}
      renderHeader={() => (
        <Typography variant="h4" color="primary" className={classes.title}>
          {title}
        </Typography>
      )}
      buttons={modalButtons}
    >
      <Box display="flex" justifyContent="center">
        <Box>
          {subTitle && <Typography variant="body2">{subTitle}</Typography>}
          <Box pt={7} pb={7} display="flex" justifyContent="center">
            {currentPage === pages.county ? (
              <CustomAutocomplete
                dataKey="county"
                value={selectedCounty}
                onChange={county => setSelectedCounty(county)}
                placeholder={placeholder}
                options={clientCountiesOptions}
                noOptionsText={noOptionsText}
              />
            ) : null}
            {currentPage === pages.topic ? (
              <CustomAutocomplete
                dataKey="topic"
                value={selectedTopic}
                onChange={topic => setSelectedTopic(topic)}
                placeholder={placeholder}
                options={clientTopicsOptions}
                noOptionsText={noOptionsText}
              />
            ) : null}
          </Box>
        </Box>
      </Box>
    </ModalDialog>
  );
};

export default CustomOnboardingDialog;
